export type OperationalErrorCode =
  | 'AUTH_REQUIRED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'VALIDATION'
  | 'RATE_LIMITED'
  | 'TIMEOUT'
  | 'NETWORK'
  | 'SERVER'
  | 'PREVIEW_UNAVAILABLE'
  | 'UNKNOWN'

export interface OperationalErrorPayload {
  message?: string
  code?: string
  requestId?: string
  retryable?: boolean
  currentRevision?: number
  details?: unknown
}

export class OperationalError extends Error {
  readonly code: OperationalErrorCode
  readonly status: number | null
  readonly requestId: string | null
  readonly retryable: boolean
  readonly payload: OperationalErrorPayload

  constructor(message: string, options: {
    code?: OperationalErrorCode
    status?: number | null
    requestId?: string | null
    retryable?: boolean
    payload?: OperationalErrorPayload
  } = {}) {
    const requestId = options.requestId ?? null
    super(requestId && !message.includes(requestId) ? `${message}（请求编号：${requestId}）` : message)
    this.name = 'OperationalError'
    this.code = options.code ?? 'UNKNOWN'
    this.status = options.status ?? null
    this.requestId = requestId
    this.retryable = options.retryable ?? isRetryableStatus(this.status)
    this.payload = options.payload ?? {message, ...(requestId ? {requestId} : {})}
  }
}

export function isOperationalError(value: unknown): value is OperationalError {
  return value instanceof OperationalError
}

export function isRetryableStatus(status: number | null | undefined): boolean {
  return status === 408 || status === 425 || status === 429 || status === 502 || status === 503 || status === 504
}

export function normalizeOperationalError(value: unknown): OperationalError {
  if (isOperationalError(value)) return value
  if (value instanceof TypeError) return new OperationalError('网络连接失败，请检查网络后重试', {code: 'NETWORK', retryable: true})
  if (value instanceof Error) return new OperationalError(value.message || '请求失败', {code: 'UNKNOWN', retryable: false})
  return new OperationalError('请求失败，请稍后重试', {code: 'UNKNOWN', retryable: false})
}

export function operationalErrorCode(status: number | null | undefined, payload: OperationalErrorPayload = {}): OperationalErrorCode {
  if (payload.code && isOperationalErrorCode(payload.code)) return payload.code
  if (status === 401) return 'AUTH_REQUIRED'
  if (status === 403) return 'FORBIDDEN'
  if (status === 404) return 'NOT_FOUND'
  if (status === 409) return 'CONFLICT'
  if (status === 422 || status === 400) return 'VALIDATION'
  if (status === 408) return 'TIMEOUT'
  if (status === 429) return 'RATE_LIMITED'
  if (status !== null && status !== undefined && status >= 500) return 'SERVER'
  return 'UNKNOWN'
}

function isOperationalErrorCode(value: string): value is OperationalErrorCode {
  return ['AUTH_REQUIRED', 'FORBIDDEN', 'NOT_FOUND', 'CONFLICT', 'VALIDATION', 'RATE_LIMITED', 'TIMEOUT', 'NETWORK', 'SERVER', 'PREVIEW_UNAVAILABLE', 'UNKNOWN'].includes(value)
}
